import React, { useState, useEffect, useCallback, useRef } from "react";

import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Container,
} from "@mui/material";

import { apiAxios } from "../../apis/apiConfig";

// ── Lightbox with zoom + swipe ──────────────────────────────────────────────
const Lightbox = ({ photos, startIndex, categoryLabel, onClose }) => {
  const [current, setCurrent] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const touchStartRef = useRef(null);
  const lastTapRef = useRef(0);
  const pinchStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const prev = useCallback(() => {
    setCurrent((i) => (i - 1 + photos.length) % photos.length);
    resetZoom();
  }, [photos.length, resetZoom]);

  const next = useCallback(() => {
    setCurrent((i) => (i + 1) % photos.length);
    resetZoom();
  }, [photos.length, resetZoom]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prev, next, onClose]);

  // Mouse wheel zoom
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      setScale((s) => {
        const newScale = s + (e.deltaY < 0 ? 0.3 : -0.3);
        const clamped = Math.min(Math.max(newScale, 1), 5);
        if (clamped === 1) setTranslate({ x: 0, y: 0 });
        return clamped;
      });
    },
    [],
  );

  // Pan when zoomed (mouse)
  const handleMouseDown = useCallback(
    (e) => {
      if (scale <= 1) return;
      e.preventDefault();
      isDraggingRef.current = true;
      dragStartRef.current = {
        x: e.clientX - translate.x,
        y: e.clientY - translate.y,
      };
    },
    [scale, translate],
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;
      setTranslate({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      });
    },
    [],
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // Touch: swipe + pinch zoom + double tap
  const handleTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 2) {
        // Pinch start
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
        pinchStartRef.current = { dist, scale };
      } else if (e.touches.length === 1) {
        const now = Date.now();
        // Double tap to toggle zoom
        if (now - lastTapRef.current < 300) {
          if (scale > 1) {
            resetZoom();
          } else {
            setScale(2.5);
          }
          lastTapRef.current = 0;
          return;
        }
        lastTapRef.current = now;

        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          translateX: translate.x,
          translateY: translate.y,
        };
      }
    },
    [scale, translate, resetZoom],
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (e.touches.length === 2 && pinchStartRef.current) {
        e.preventDefault();
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
        const ratio = dist / pinchStartRef.current.dist;
        const newScale = Math.min(Math.max(pinchStartRef.current.scale * ratio, 1), 5);
        setScale(newScale);
        if (newScale === 1) setTranslate({ x: 0, y: 0 });
      } else if (e.touches.length === 1 && touchStartRef.current && scale > 1) {
        // Pan while zoomed
        setTranslate({
          x: touchStartRef.current.translateX + (e.touches[0].clientX - touchStartRef.current.x),
          y: touchStartRef.current.translateY + (e.touches[0].clientY - touchStartRef.current.y),
        });
      }
    },
    [scale],
  );

  const handleTouchEnd = useCallback(
    (e) => {
      if (pinchStartRef.current && e.touches.length < 2) {
        pinchStartRef.current = null;
        return;
      }

      if (!touchStartRef.current || scale > 1) {
        touchStartRef.current = null;
        return;
      }

      // Swipe detection (only when not zoomed)
      const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
      const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
      touchStartRef.current = null;

      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx > 0) prev();
        else next();
      }
    },
    [scale, prev, next],
  );

  if (!photos.length) return null;

  const photo = photos[current];

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(0,0,0,0.95)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* TOP BAR */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          flexShrink: 0,
        }}
      >
        <Box
          onClick={onClose}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            color: "#fff",
            fontSize: { xs: "0.75rem", md: "0.9rem" },
            fontWeight: 600,
            px: 2,
            py: 0.75,
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "4px",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          ← Back to Gallery
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: "#C59324",
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            {categoryLabel}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.8rem",
              mt: 0.25,
            }}
          >
            {current + 1} / {photos.length}
          </Typography>
        </Box>

        <Box
          onClick={onClose}
          sx={{
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            fontSize: "1.5rem",
            lineHeight: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: "4px",
            "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          ✕
        </Box>
      </Box>

      {/* IMAGE AREA */}
      <Box
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          px: { xs: 1, md: 8 },
          overflow: "hidden",
          cursor: scale > 1 ? "grab" : "default",
          touchAction: "none",
        }}
      >
        {/* PREV */}
        {photos.length > 1 && (
          <Box
            onClick={prev}
            sx={{
              position: "absolute",
              left: { xs: 4, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: "1.4rem",
              borderRadius: "50%",
              cursor: "pointer",
              zIndex: 2,
              "&:hover": { bgcolor: "#C59324", color: "#000" },
            }}
          >
            ‹
          </Box>
        )}

        <Box
          ref={imageRef}
          component="img"
          src={photo.url}
          alt={photo.caption || `Photo ${current + 1}`}
          draggable={false}
          sx={{
            maxWidth: "100%",
            maxHeight: "calc(100vh - 160px)",
            objectFit: "contain",
            borderRadius: "4px",
            userSelect: "none",
            transition: scale === 1 ? "transform 0.2s ease" : "none",
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
          }}
        />

        {/* NEXT */}
        {photos.length > 1 && (
          <Box
            onClick={next}
            sx={{
              position: "absolute",
              right: { xs: 4, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: "1.4rem",
              borderRadius: "50%",
              cursor: "pointer",
              zIndex: 2,
              "&:hover": { bgcolor: "#C59324", color: "#000" },
            }}
          >
            ›
          </Box>
        )}

        {/* Zoom hint */}
        {scale === 1 && (
          <Typography
            sx={{
              position: "absolute",
              bottom: 8,
              right: { xs: 8, md: 32 },
              color: "rgba(255,255,255,0.35)",
              fontSize: "0.7rem",
            }}
          >
            Scroll to zoom · Double-tap on mobile
          </Typography>
        )}
      </Box>

      {/* CAPTION */}
      {photo.caption && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            textAlign: "center",
            px: 3,
            py: 1.5,
            flexShrink: 0,
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography
            sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.88rem" }}
          >
            {photo.caption}
          </Typography>
        </Box>
      )}

      {/* THUMBNAILS */}
      {photos.length > 1 && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            px: 2,
            py: 1.5,
            flexShrink: 0,
            overflowX: "auto",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {photos.map((p, i) => (
            <Box
              key={i}
              onClick={() => {
                setCurrent(i);
                resetZoom();
              }}
              component="img"
              src={p.url}
              alt=""
              sx={{
                width: 52,
                height: 40,
                objectFit: "cover",
                borderRadius: "3px",
                cursor: "pointer",
                opacity: i === current ? 1 : 0.4,
                border:
                  i === current
                    ? "2px solid #C59324"
                    : "2px solid transparent",
                transition: "all 0.2s",
                flexShrink: 0,
                "&:hover": { opacity: 0.9 },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Gallery Card ──────────────────────────────────────────────────────────────
const GalleryCard = ({ label, color, photos, onOpen }) => {
  const hasPhotos = photos && photos.length > 0;
  const cover = hasPhotos ? photos[0] : null;

  return (
    <Card
      onClick={() => hasPhotos && onOpen(photos, 0)}
      sx={{
        borderRadius: "4px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        overflow: "hidden",
        border: "1px solid #eee",
        cursor: hasPhotos ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": hasPhotos
          ? {
              transform: "translateY(-4px)",
              boxShadow: "0 10px 28px rgba(0,0,0,0.12)",
            }
          : {},
      }}
    >
      <Box
        sx={{
          height: { xs: "180px", sm: "220px", md: "250px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          background: color || "#eee",
        }}
      >
        {cover ? (
          <Box
            component="img"
            src={cover.url}
            alt={label}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <>
            <Typography sx={{ fontSize: "40px", opacity: 0.7 }}>📷</Typography>
            <Typography sx={{ color: "#888", fontSize: "12px", mt: 1 }}>
              {label}
            </Typography>
          </>
        )}

        {hasPhotos && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              bgcolor: "rgba(0,0,0,0.65)",
              color: "#fff",
              fontSize: "11px",
              px: 1.5,
              py: 0.5,
              borderRadius: "4px",
              fontWeight: 600,
            }}
          >
            {photos.length} photo{photos.length !== 1 ? "s" : ""}
          </Box>
        )}
      </Box>

      <CardContent sx={{ textAlign: "center", py: "12px !important" }}>
        <Typography
          sx={{ fontSize: "13px", fontWeight: "bold", color: "#222" }}
        >
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Card background colors for visual variety
const CARD_COLORS = [
  "#E8E2D5",
  "#E0D8C8",
  "#D8D5CC",
  "#D4D9D2",
  "#E5E0D8",
  "#DED8CB",
  "#D0CEC8",
  "#E2DDD5",
];

// ── MAIN ──────────────────────────────────────────────────────────────────────
const CampaignGallerySection = () => {
  const [categories, setCategories] = useState([]);
  const [photosByCategory, setPhotosByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  const [lightbox, setLightbox] = useState({
    open: false,
    photos: [],
    index: 0,
    label: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, photoRes] = await Promise.all([
          apiAxios.get("/gallery-categories"),
          apiAxios.get("/gallery"),
        ]);

        if (catRes.data.success) {
          setCategories(catRes.data.categories || []);
        }

        if (photoRes.data.success && photoRes.data.photos) {
          const grouped = {};
          photoRes.data.photos.forEach((photo) => {
            const category = photo.eventName || "Other";
            if (!grouped[category]) grouped[category] = [];
            grouped[category].push(photo);
          });
          setPhotosByCategory(grouped);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openLightbox = (photos, index, label) =>
    setLightbox({ open: true, photos, index, label });

  const closeLightbox = useCallback(
    () => setLightbox({ open: false, photos: [], index: 0, label: "" }),
    [],
  );

  // Only show categories that have photos
  const visibleCategories = categories.filter(
    (cat) => photosByCategory[cat.name] && photosByCategory[cat.name].length > 0,
  );

  return (
    <>
      <Box
        id="photos"
        sx={{
          py: { xs: 6, md: 8 },
          background: "#fff",
          fontFamily: "'Playfair Display', serif",
        }}
      >
        <Container maxWidth="lg">
          {/* HEADING */}
          <Box sx={{ marginBottom: "40px" }}>
            <Typography
              sx={{
                color: "#C59324",
                fontWeight: 700,
                letterSpacing: "0.2em",
                fontSize: "0.85rem",
                textTransform: "uppercase",
                mb: 2,
              }}
            >
              Campaign Moments
            </Typography>

            <Typography
              variant="h3"
              sx={{
                mb: "8px",
                color: "#333",
                fontWeight: 700,
                fontFamily: "'Playfair Display', serif",
                mt: 1,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              Gary{" "}
              <span
                style={{
                  color: "#C59324",
                  fontStyle: "italic",
                  fontWeight: 500,
                  fontFamily: "'Playfair Display', serif",
                }}
              >
                in the Community
              </span>
            </Typography>

            <Typography
              sx={{
                color: "#666",
                maxWidth: "500px",
                mt: 2,
                lineHeight: 1.6,
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
              }}
            >
              From neighborhood events to community conversations; follow Gary's
              journey through Middletown.
            </Typography>
          </Box>

          {/* LOADER */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#C59324" }} />
            </Box>
          ) : visibleCategories.length === 0 ? (
            <Typography
              sx={{ textAlign: "center", color: "#888", py: 6 }}
            >
              Gallery photos coming soon.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: "20px",
              }}
            >
              {visibleCategories.map((cat, idx) => (
                <GalleryCard
                  key={cat._id}
                  label={cat.name}
                  color={CARD_COLORS[idx % CARD_COLORS.length]}
                  photos={photosByCategory[cat.name] || []}
                  onOpen={(p, i) => openLightbox(p, i, cat.name)}
                />
              ))}
            </Box>
          )}
        </Container>
      </Box>

      {/* LIGHTBOX */}
      {lightbox.open && (
        <Lightbox
          photos={lightbox.photos}
          startIndex={lightbox.index}
          categoryLabel={lightbox.label}
          onClose={closeLightbox}
        />
      )}
    </>
  );
};

export default CampaignGallerySection;
